import { Fragment, useEffect, useState } from "react";
import { useAuth, roleLabel } from "../lib/AuthContext";
import {
  createUserApi,
  deleteUserApi,
  fetchSections,
  fetchUsers,
  patchUserApi,
  resetUserPasswordApi,
  type SectionDef,
  type UserRecord,
  type UserRole,
} from "../lib/authApi";

/**
 * Gestión de usuarios de la aplicación (SOLO admin). Permite:
 *  - alta con contraseña temporal (mustChangePassword=true por defecto)
 *  - cambio de rol y activo/inactivo
 *  - reset de contraseña (fija una nueva y fuerza cambio al siguiente login)
 *  - borrado (con guarda "no puedes borrarte a ti mismo ni al último admin")
 */

const ROLE_OPTIONS: UserRole[] = ["admin", "contable", "consulta"];

export function Users(): JSX.Element {
  const { user: current } = useAuth();
  const [items, setItems] = useState<UserRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<{
    username: string;
    displayName: string;
    password: string;
    role: UserRole;
  }>({ username: "", displayName: "", password: "", role: "consulta" });
  const [busy, setBusy] = useState(false);
  const [sections, setSections] = useState<SectionDef[]>([]);
  /** Usuario cuyo editor de permisos está abierto + selección en edición. */
  const [editing, setEditing] = useState<string | null>(null);
  const [editSel, setEditSel] = useState<Set<string>>(new Set());

  const load = () => {
    setError(null);
    fetchUsers()
      .then(setItems)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(load, []);
  useEffect(() => {
    fetchSections().then(setSections).catch(() => setSections([]));
  }, []);

  function openPermissions(u: UserRecord) {
    if (editing === u.username) {
      setEditing(null);
      return;
    }
    setEditing(u.username);
    setEditSel(new Set(u.sections ?? []));
  }

  function toggleSection(id: string) {
    setEditSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function savePermissions(u: UserRecord, sectionsToSave: string[] | null) {
    try {
      await patchUserApi(u.username, { sections: sectionsToSave });
      setEditing(null);
      load();
    } catch (e: unknown) {
      alert(`No se pudieron guardar los permisos: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function save() {
    if (!draft.username.trim() || !draft.password) {
      alert("Indica al menos usuario y contraseña.");
      return;
    }
    setBusy(true);
    try {
      await createUserApi({
        username: draft.username.trim(),
        displayName: draft.displayName.trim(),
        password: draft.password,
        role: draft.role,
      });
      setDraft({ username: "", displayName: "", password: "", role: "consulta" });
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear usuario: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function onChangeRole(u: UserRecord, role: UserRole) {
    try {
      await patchUserApi(u.username, { role });
      load();
    } catch (e: unknown) {
      alert(`No se pudo cambiar el rol: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function onToggleActive(u: UserRecord) {
    try {
      await patchUserApi(u.username, { active: !u.active });
      load();
    } catch (e: unknown) {
      alert(`No se pudo cambiar el estado: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function onResetPassword(u: UserRecord) {
    const pw = window.prompt(
      `Nueva contraseña temporal para "${u.username}".\n` +
        `Se le forzará a cambiarla al siguiente inicio de sesión.`,
    );
    if (!pw) return;
    if (pw.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    try {
      await resetUserPasswordApi(u.username, pw);
      alert("Contraseña restablecida.");
    } catch (e: unknown) {
      alert(`No se pudo resetear: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function onDelete(u: UserRecord) {
    if (!window.confirm(`¿Eliminar al usuario "${u.username}"?`)) return;
    try {
      await deleteUserApi(u.username);
      load();
    } catch (e: unknown) {
      alert(`No se pudo borrar: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Usuarios</h2>
          <p className="meta">
            Alta de usuarios y perfiles. Al crear un usuario se le asigna una
            contraseña temporal; en su primer inicio de sesión se le pedirá que
            la cambie.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={() => setCreating((x) => !x)}>
            {creating ? "✕ Cancelar" : "+ Nuevo usuario"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {creating && (
        <div className="form-row" style={{ marginBottom: "1rem" }}>
          <input
            placeholder="Usuario (login)"
            value={draft.username}
            onChange={(e) =>
              setDraft({ ...draft, username: e.target.value.toLowerCase() })
            }
          />
          <input
            placeholder="Nombre visible"
            value={draft.displayName}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
          />
          <input
            type="password"
            placeholder="Contraseña temporal"
            value={draft.password}
            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
          />
          <select
            value={draft.role}
            onChange={(e) => setDraft({ ...draft, role: e.target.value as UserRole })}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
          <button type="button" className="primary" onClick={save} disabled={busy}>
            {busy ? "Creando…" : "Crear"}
          </button>
        </div>
      )}

      {error && <div className="warning">Error: {error}</div>}

      {!items && !error && <p className="meta">Cargando…</p>}

      {items && (
        <table className="data">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Rol</th>
              <th>Apartados</th>
              <th>Estado</th>
              <th>Último acceso</th>
              <th style={{ width: 260 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const isSelf = current?.username === u.username;
              return (
                <Fragment key={u.username}>
                <tr>
                  <td><code>{u.username}</code>{isSelf && <span className="badge"> tú</span>}</td>
                  <td>{u.displayName}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) => onChangeRole(u, e.target.value as UserRole)}
                      disabled={isSelf}
                      title={isSelf ? "No puedes cambiar tu propio rol" : ""}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{roleLabel(r)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {u.role === "admin" ? (
                      <span className="meta">Todos</span>
                    ) : (
                      <>
                        {(u.sections ?? []).length} de {sections.length}
                        {u.customSections && (
                          <span className="badge" title="Personalizado por un administrador"> personalizado</span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {u.active ? (
                      <span style={{ color: "var(--ok-700)" }}>Activo</span>
                    ) : (
                      <span style={{ color: "var(--ink-500)" }}>Inactivo</span>
                    )}
                    {u.mustChangePassword && (
                      <div style={{ fontSize: "0.75rem", color: "var(--warn-700)" }}>
                        Debe cambiar contraseña
                      </div>
                    )}
                  </td>
                  <td>
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleString("es-ES")
                      : <span className="meta">nunca</span>}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      {u.role !== "admin" && (
                        <button type="button" onClick={() => openPermissions(u)}>
                          {editing === u.username ? "Cerrar permisos" : "Permisos"}
                        </button>
                      )}
                      <button type="button" onClick={() => onResetPassword(u)}>
                        Resetear contraseña
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleActive(u)}
                        disabled={isSelf}
                        title={isSelf ? "No puedes desactivarte a ti mismo" : ""}
                      >
                        {u.active ? "Desactivar" : "Activar"}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => onDelete(u)}
                        disabled={isSelf}
                        title={isSelf ? "No puedes borrarte a ti mismo" : ""}
                      >
                        Borrar
                      </button>
                    </div>
                  </td>
                </tr>
                {editing === u.username && (
                  <tr>
                    <td colSpan={7} style={{ background: "var(--ink-50, #f7f7f8)" }}>
                      <p className="meta" style={{ margin: "0.25rem 0 0.5rem" }}>
                        Marca los apartados a los que <strong>{u.username}</strong> tiene acceso.
                        Escribir/contabilizar sigue dependiendo de su rol ({roleLabel(u.role)}).
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                          gap: "0.35rem 1rem",
                          marginBottom: "0.6rem",
                        }}
                      >
                        {sections.map((sec) => (
                          <label key={sec.id} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <input
                              type="checkbox"
                              checked={editSel.has(sec.id)}
                              onChange={() => toggleSection(sec.id)}
                            />
                            {sec.label}
                          </label>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <button type="button" className="primary" onClick={() => savePermissions(u, [...editSel])}>
                          Guardar permisos
                        </button>
                        <button type="button" onClick={() => setEditSel(new Set(sections.map((x) => x.id)))}>
                          Marcar todo
                        </button>
                        <button type="button" onClick={() => setEditSel(new Set())}>Quitar todo</button>
                        <button
                          type="button"
                          onClick={() => savePermissions(u, null)}
                          title="Volver a los apartados que da el rol por defecto"
                        >
                          Restablecer a los del rol
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
